/**
 * Pure builder: store snapshot + master-time → `FrameDescriptor`.
 *
 * Used by:
 *   - `PreviewRuntime` on every RAF tick to drive the backend.
 *   - Tests, which can call this without a Zustand store / React tree.
 *   - Export-side wrapper (Schritt 8) which re-uses the layer/fx logic
 *     but supplies its own intrinsic-rotation map for source frames.
 *
 * No side effects, no DOM access. Returns a fresh descriptor on every
 * call — caller decides about diffing.
 */
import type { Clip, ExportSpec, Pill, Segment } from "../types";
import { isImageClip, normaliseRotation } from "../types";
import type { Cut } from "../../storage/jobs-db";
import type { FilterSlot, FxKind, PunchFx } from "../fx/types";
import { activeCamAtArr } from "../arrangement-pills";
import { masterToArr } from "../arrangement-time";
import { activeFxAt } from "../fx/active";
import { fxCatalog } from "../fx/catalog";
import { INSTANT_ENVELOPE, type ADSREnvelope } from "../fx/envelope";
import {
  computeIntensity,
  followerFor,
  legacyModulation,
  type AudioEnvelope,
  type ModConfig,
  type Modulation,
} from "../fx/modulation";
import { gradeIsIdentity, type GradeParams } from "../fx/looks";
import { resolveOutputDims } from "../output-frame";
import {
  buildElementFitRect,
  DEFAULT_VIEWPORT_TRANSFORM,
} from "./element-transform";
import type {
  FitRect,
  FrameDescriptor,
  FrameFx,
  FrameLayer,
  OutputDims,
} from "./frame-descriptor";

/** Mirror of the FxHoldEntry shape from the store. Replicated locally
 *  so the descriptor builder stays free of cross-imports back into the
 *  Zustand slice. */
export interface FxHoldSnapshot {
  mode: "persistent" | "preview";
  kind: FxKind;
  fxId: string;
  startS: number;
}

/** Minimal store-snapshot shape the builder needs. Lets tests construct
 *  inputs without instantiating the full Zustand store. */
export interface EditorStoreSnapshot {
  clips: readonly Clip[];
  cuts: readonly Cut[];
  fx: readonly PunchFx[];
  exportSpec: ExportSpec;
  /** Optional. When non-empty (alongside non-empty `arrangementSegments`)
   *  the descriptor builder dispatches each preview frame's active cam
   *  through `activeCamAtArr` so per-pill source-trim is honoured. */
  pills?: readonly Pill[];
  /** Optional. Required for pill-aware rendering — the audio walker's
   *  segment list governs the master↔arr mapping. */
  arrangementSegments?: readonly Segment[];
  /** Active live holds keyed by slot — `mode: "preview"` entries are
   *  synthesised into transient FrameFx (no timeline write). Optional so
   *  test stubs and the export compositor can omit it. */
  fxHolds?: Readonly<Record<string, FxHoldSnapshot>>;
  /** The kind currently bound to the panel's encoders. While set, any
   *  active fx of this kind has its `params`/`envelope` overridden with
   *  the live encoder + ADSR-editor values, so encoder turns reflect in
   *  the preview without re-recording. Optional. */
  selectedFxKind?: FxKind | null;
  /** Per-kind live encoder values (storage-range). When `selectedFxKind`
   *  matches an active fx, this map provides the override params. */
  fxDefaults?: Readonly<Partial<Record<FxKind, Record<string, number>>>>;
  /** Per-kind live ADSR envelope values. Same override scope as
   *  fxDefaults — only the selected kind's envelope is overridden. */
  fxEnvelopes?: Readonly<Partial<Record<FxKind, ADSREnvelope>>>;
  /** Per-kind live modulator config (timeMod/depth/lfo/side). For the
   *  selected kind this overrides active fx so M-screen edits show live. */
  fxModulations?: Readonly<Partial<Record<FxKind, ModConfig>>>;
  /** Real song tempo for beat-synced modulation. null/absent disables
   *  beat-sync (the LFO falls back to a 120-BPM-equivalent division). */
  bpm?: number | null;
  /** Master-time of beat 0 — the grid anchor for beat-synced LFO phase. */
  beatPhaseS?: number;
  beatsPerBar?: number;
  /** Normalized master-loudness curve (0..1 over master-time) driving
   *  sidechain modulation. Absent → sidechain reads silence. */
  audioEnv?: AudioEnvelope | null;
  /** Single global color grade — the corrective/creative pass. Emitted as
   *  one `grade` FrameFx prepended ahead of everything (skipped when it's the
   *  identity). Optional so test stubs / the export compositor can omit it. */
  colorGrade?: GradeParams;
  /** Opinionated filter stack — each active slot becomes one FrameFx of its
   *  own kind, emitted AFTER the grade and BEFORE the punch-in accents, so
   *  the order is: grade → filters → accents. Optional. */
  filterSlots?: readonly FilterSlot[];
}

/**
 * Build a descriptor for the LIVE PREVIEW.
 *
 * Source assumption: `<video>` elements supplied at draw time have the
 * intrinsic MP4 rotation matrix already applied by the browser (i.e.
 * `videoWidth/Height` already swapped). So `rotationDeg` here is the
 * USER rotation only. The export builder (Schritt 8) takes a different
 * code path that adds the intrinsic on top.
 *
 * Time arguments:
 *   - `tMaster` is the master-audio source-time. Kept on the descriptor
 *     so backends that need it for diagnostics still see it; not used
 *     for any pill / cut / fx resolution anymore.
 *   - `tTimeline` is the walker's authoritative timeline-position. ALL
 *     resolution (pill picker, cuts, FX) anchors here so duplicate
 *     pills in the song stay distinct. When the caller omits it (legacy
 *     callers, test stubs) we fall back to projecting `tMaster` — works
 *     for non-duplicate jobs, returns the FIRST occurrence's slot for
 *     duplicates. The runtime always supplies it.
 */
export function buildPreviewFrameDescriptor(
  snapshot: EditorStoreSnapshot,
  tMaster: number,
  tTimeline?: number,
): FrameDescriptor {
  const output = computeOutputSnapped(snapshot.clips, snapshot.exportSpec.resolution);
  const segments = snapshot.arrangementSegments ?? [];
  const tArr = tTimeline ?? masterToArr(tMaster, segments);
  const fxOut = buildFx(snapshot, tMaster, tArr);

  if (!output) {
    return { tMaster, output: null, layers: [], fx: fxOut };
  }

  const pills = snapshot.pills ?? [];
  const active = activeCamAtArr(snapshot.cuts, tArr, pills, segments);
  let layers: FrameLayer[] = [];
  if (active) {
    layers = buildPreviewLayersFromPill(
      snapshot.clips,
      active.camId,
      active.pill,
      tArr,
      output,
    );
  }

  return { tMaster, output, layers, fx: fxOut };
}

// ---------- internals ----------

function computeOutputSnapped(
  clips: readonly Clip[],
  resolution: ExportSpec["resolution"],
): OutputDims | null {
  const raw = resolveOutputDims(clips, resolution);
  if (!raw) return null;
  return { w: Math.round(raw.w), h: Math.round(raw.h) };
}

/** Build preview layers from the active pill. The pill's `sourceIn/Out`
 *  is the authoritative source-time mapping (user-edited via the
 *  Timeline's pill-trim handles), so we read it directly and ignore
 *  the cam's master-time anchor. */
function buildPreviewLayersFromPill(
  clips: readonly Clip[],
  activeId: string,
  pill: Pill,
  tArr: number,
  output: OutputDims,
): FrameLayer[] {
  const clip = clips.find((c) => c.id === activeId);
  if (!clip) return [];
  const userRot = normaliseRotation(clip.rotation);
  const flipX = !!clip.flipX;
  const flipY = !!clip.flipY;
  const baseW = clip.displayW ?? 0;
  const baseH = clip.displayH ?? 0;
  if (baseW <= 0 || baseH <= 0) return [];
  const swap = userRot === 90 || userRot === 270;
  const dispW = swap ? baseH : baseW;
  const dispH = swap ? baseW : baseH;
  const fitRect: FitRect = buildElementFitRect(
    { w: dispW, h: dispH },
    { w: output.w, h: output.h },
    clip.viewportTransform ?? DEFAULT_VIEWPORT_TRANSFORM,
  );
  // Linear interpolation between the pill's source endpoints. Bakes any
  // driftRatio into the pill's `sourceOutS - sourceInS` span, which is
  // wider/narrower than `arrEndS - arrStartS` whenever drift ≠ 1. A
  // naive `sourceIn + (tArr - arrStart)` would silently ignore drift and
  // produce wrong source frames on a drifted cam.
  const arrSpan = pill.arrEndS - pill.arrStartS;
  const sourceTimeS =
    arrSpan > 0
      ? pill.sourceInS +
        ((tArr - pill.arrStartS) * (pill.sourceOutS - pill.sourceInS)) /
          arrSpan
      : pill.sourceInS;
  const source = isImageClip(clip)
    ? { kind: "image" as const, clipId: clip.id }
    : {
        kind: "video" as const,
        clipId: clip.id,
        sourceTimeS,
        sourceDurS: clip.sourceDurationS,
      };
  return [
    {
      layerId: clip.id,
      source,
      weight: 1,
      fitRect,
      rotationDeg: userRot,
      flipX,
      flipY,
      displayW: dispW,
      displayH: dispH,
    },
  ];
}

/**
 * The single global color grade → at most one `grade` FrameFx (inS 0,
 * wetness 1). Skipped entirely when the grade is the identity. Shared by the
 * live preview (buildFx) AND the export compositor so preview == render.
 */
export function colorGradeFrameFx(grade: GradeParams | undefined): FrameFx[] {
  if (!grade || gradeIsIdentity(grade)) return [];
  return [
    {
      id: "grade",
      kind: "grade",
      inS: 0,
      // GradeParams is structurally all-number; FrameFx.params is a plain
      // Record the backends read by key.
      params: grade as unknown as Record<string, number>,
      wetness: 1,
    },
  ];
}

/**
 * The opinionated filter stack → one FrameFx per active slot (amount>0), in
 * slot order. Each filter is a replace-blend source-FX, so slots compose
 * serially top→bottom (each filters the previous one's output) — reordering
 * genuinely changes the result. Params are merged with the kind's defaults.
 * Shared by preview + export.
 */
export function filterFrameFx(
  slots: readonly FilterSlot[] | undefined,
): FrameFx[] {
  if (!slots) return [];
  const out: FrameFx[] = [];
  for (const slot of slots) {
    const def = fxCatalog[slot.kind];
    if (!def) continue;
    const params = { ...def.defaultParams, ...slot.params };
    if ((params.amount ?? 1) <= 0) continue;
    out.push({ id: slot.id, kind: slot.kind, inS: 0, params, wetness: 1 });
  }
  return out;
}

function buildFx(
  snapshot: EditorStoreSnapshot,
  tMaster: number,
  tTimeline: number,
): FrameFx[] {
  // Order: color grade (corrective pass) → opinionated filters (the looks) →
  // punch-in accents on top. All replace-blend, re-snapshotted per FX, so
  // they compose serially.
  const out: FrameFx[] = [
    ...colorGradeFrameFx(snapshot.colorGrade),
    ...filterFrameFx(snapshot.filterSlots),
  ];

  const selectedKind = snapshot.selectedFxKind ?? null;
  const overrideParams =
    selectedKind != null ? snapshot.fxDefaults?.[selectedKind] : undefined;
  const overrideEnv =
    selectedKind != null ? snapshot.fxEnvelopes?.[selectedKind] : undefined;
  const hasParamOverride = !!(
    overrideParams && Object.keys(overrideParams).length > 0
  );
  const bpm = snapshot.bpm ?? null;
  const beatPhaseS = snapshot.beatPhaseS ?? 0;
  const beatsPerBar = snapshot.beatsPerBar ?? 4;

  // Currently-held PunchFx ids (persistent holds only — preview holds
  // have no fxId). While held, envelope sampling skips the release phase
  // so the effect sits at sustain level until the user lets go (synth-
  // voice semantics). Without this the live-extended `outS` keeps the
  // sample point inside the release window for the entire hold and the
  // effect renders much weaker than its sustain level.
  const heldIds = new Set<string>();
  if (snapshot.fxHolds) {
    for (const h of Object.values(snapshot.fxHolds)) {
      if (h.mode === "persistent" && h.fxId) heldIds.add(h.fxId);
    }
  }

  // Persistent: real PunchFx capsules on the timeline. Intensity is the
  // unified envelope ⊗ (lfo|sidechain); legacy fx (no `modulation`) route
  // through a depth-0 wrapper → identical to the old envelope-only path.
  for (const f of activeFxAt(snapshot.fx, tTimeline)) {
    const def = fxCatalog[f.kind];
    const useOverride = selectedKind === f.kind;
    const baseParams = f.params ?? {};
    const merged: Record<string, number> = {
      ...def.defaultParams,
      ...(useOverride && hasParamOverride ? overrideParams : baseParams),
    };
    const env =
      (useOverride ? overrideEnv : undefined) ??
      f.modulation?.envelope ??
      f.envelope ??
      INSTANT_ENVELOPE;
    // While a kind is selected, the M-screen's live modulator config drives
    // the preview (recording-head); committed fx keep their baked modulation.
    const liveModCfg = useOverride ? snapshot.fxModulations?.[f.kind] : undefined;
    let mod: Modulation;
    if (liveModCfg) {
      mod = { envelope: env, ...liveModCfg };
    } else if (f.modulation) {
      // Live ADSR-editor edits still override the active kind's envelope.
      mod = useOverride && overrideEnv ? { ...f.modulation, envelope: env } : f.modulation;
    } else {
      mod = legacyModulation(env);
    }
    const holding = heldIds.has(f.id);
    const sidechainCurve =
      mod.timeMod === "sidechain" ? followerFor(snapshot.audioEnv, mod.side) : null;
    const { level, phase } = computeIntensity(mod, {
      tMasterS: tMaster,
      tTimelineS: tTimeline,
      regionInS: f.inS,
      regionDurS: f.outS - f.inS,
      holding,
      bpm,
      beatPhaseS,
      beatsPerBar,
      sidechainCurve,
    });
    if (level <= 0) continue;
    // Per-kind intensity application — each effect knows how to dim itself
    // intelligently. Generic alpha-blend over source doesn't work for
    // displacement effects (zoom would ghost), so kinds ship their own
    // scaling in `def.applyWetness`.
    const params =
      def.applyWetness && level < 1 ? def.applyWetness(merged, level) : merged;
    out.push({
      id: f.id,
      kind: f.kind,
      inS: f.inS,
      params,
      wetness: level,
      phase,
    });
  }

  // Preview holds: while playback is paused, pad-presses overlay the
  // effect at full strength on the live frame without writing anything
  // to fx[]. Synthesised here as transient FrameFx with wetness=1 so the
  // user can dial macros with full visual feedback.
  if (snapshot.fxHolds) {
    for (const hold of Object.values(snapshot.fxHolds)) {
      if (hold.mode !== "preview") continue;
      const def = fxCatalog[hold.kind];
      if (!def) continue;
      const live = snapshot.fxDefaults?.[hold.kind];
      const params: Record<string, number> = {
        ...def.defaultParams,
        ...(live ?? {}),
      };
      out.push({
        id: `preview:${hold.kind}:${hold.fxId || hold.startS}`,
        kind: hold.kind,
        inS: hold.startS,
        params,
        wetness: 1,
        phase: 0,
      });
    }
  }

  return out;
}

// `buildElementFitRect` lives in `./element-transform` — re-exported there
// so both preview (this file) and export (`local/render/compositor.ts`)
// hit the same implementation. Don't add a second copy here.
export { buildElementFitRect } from "./element-transform";
